import { Division, PlayerStats } from "../types";

export interface PromotionRelegationResult {
  playerId: string;
  playerName: string;
  currentDivision: Division;
  newDivision: Division;
  transitionType: "promoted" | "relegated" | "stays";
  position: number;
  points: number;
}

/**
 * Get number of promotion spots for a division
 */
export const getPromotionSpots = (division: Division): number => {
  switch (division) {
    case "Division 1":
      return 2; // Top 2 to Master
    case "Division 2":
      return 3; // Top 3 to Division 1 (legacy division, no longer in the live ladder)
    case "Division 2A":
      return 2; // Top 2 to Division 1
    case "Division 2B":
      return 2; // Top 2 to Division 2A
    case "Division 3":
      return 4; // Top 4 to Division 2B
    case "Division 4":
      return 4; // Top 4 to Division 3
    case "Master":
      return 0; // No promotion from Master (top division)
    case "Women Division":
      return 0; // No promotion from Women Division (top women division)
    case "Women Division 2":
      return 2; // Top 2 to Women Division
    default:
      return 0;
  }
};

/**
 * Get number of relegation spots for a division
 */
export const getRelegationSpots = (division: Division): number => {
  switch (division) {
    case "Master":
      return 2; // Bottom 2 to Division 1
    case "Division 1":
      return 3; // Bottom 3 to Division 2A
    case "Division 2":
      return 4; // Bottom 4 to Division 3 (legacy division, no longer in the live ladder)
    case "Division 2A":
      return 3; // Bottom 3 to Division 2B
    case "Division 2B":
      return 3; // Bottom 3 to Division 3
    case "Division 3":
      return 3; // Bottom 3 to Division 4
    case "Division 4":
      return 0; // No relegation from Division 4 (lowest)
    case "Women Division":
      return 2; // Bottom 2 to Women Division 2
    case "Women Division 2":
      return 0; // No relegation from Women Division 2
    default:
      return 0;
  }
};

/**
 * Get the promotion target division.
 *
 * The live ladder is a single linear chain (highest to lowest):
 *   Master - Division 1 - Division 2A - Division 2B - Division 3 - Division 4
 * Going up this list is promotion, going down is relegation. Each division
 * promotes into exactly the one directly above it.
 *
 * "Division 2" is a legacy, pre-split division no longer part of the live
 * ladder; its mapping is kept only so old historical data still resolves to
 * something sensible, not because it's a live feeder into Division 1.
 */
export const getPromotionTarget = (division: Division): Division | null => {
  switch (division) {
    case "Division 1":
      return "Master";
    case "Division 2":
      return "Division 1"; // legacy division, not part of the live ladder
    case "Division 2A":
      return "Division 1";
    case "Division 2B":
      return "Division 2A";
    case "Division 3":
      return "Division 2B";
    case "Division 4":
      return "Division 3";
    case "Women Division 2":
      return "Women Division";
    default:
      return null;
  }
};

/**
 * Get the relegation target division. Mirrors getPromotionTarget, going down
 * the same linear ladder instead of up.
 */
export const getRelegationTarget = (division: Division): Division | null => {
  switch (division) {
    case "Master":
      return "Division 1";
    case "Division 1":
      return "Division 2A";
    case "Division 2":
      return "Division 3"; // legacy division, not part of the live ladder
    case "Division 2A":
      return "Division 2B";
    case "Division 2B":
      return "Division 3";
    case "Division 3":
      return "Division 4";
    case "Women Division":
      return "Women Division 2";
    default:
      return null;
  }
};

// A player is also relegated (independent of table position) if their win
// rate is below 40%, once they've played enough league days for that number
// to be meaningful. Uses the same raw win % shown as "WR" on the public
// rankings page, so the relegation tag always matches the visible number.
const WIN_RATE_RELEGATION_THRESHOLD = 40;
const MIN_LEAGUE_DAYS_FOR_WIN_RATE_RELEGATION = 2;
export const isLowWinRateRelegation = (player: PlayerStats): boolean => {
  return (
    !player.isGuest &&
    !player.isGuestPlayer &&
    player.leagueDaysPlayed >= MIN_LEAGUE_DAYS_FOR_WIN_RATE_RELEGATION &&
    (player.winPercentage ?? 100) < WIN_RATE_RELEGATION_THRESHOLD
  );
};

/**
 * Actual number of players relegated out of a division: the defined
 * "bottom N" spots, plus anyone additionally flagged by the low-win-rate
 * rule (who may sit above that cutoff). This can exceed getRelegationSpots().
 */
export const getActualRelegationCount = (
  divisionRankings: PlayerStats[],
  division: Division
): number => {
  const relegationSpots = getRelegationSpots(division);
  const eligiblePlayers = divisionRankings.filter(
    (p) => !p.isGuest && !p.isGuestPlayer && p.gamesPlayed > 0
  );
  if (eligiblePlayers.length === 0) return 0;

  let count = 0;
  eligiblePlayers.forEach((player, index) => {
    const position = index + 1;
    const isPositionRelegation =
      relegationSpots > 0 &&
      position > eligiblePlayers.length - relegationSpots;
    if (isPositionRelegation || isLowWinRateRelegation(player)) {
      count++;
    }
  });
  return count;
};

// Divisions in the live promotion ladder (see getPromotionTarget). Used only
// to count, for a given target division, how many other divisions promote
// into it. Deliberately excludes the legacy "Division 2", which isn't part
// of the live ladder and would otherwise make Division 1 look like it has
// two feeders instead of one.
const PROMOTION_SOURCE_DIVISIONS: Division[] = [
  "Division 1",
  "Division 2A",
  "Division 2B",
  "Division 3",
  "Division 4",
  "Women Division 2",
];

const countFeedersInto = (target: Division): number =>
  PROMOTION_SOURCE_DIVISIONS.filter((d) => getPromotionTarget(d) === target)
    .length;

/**
 * Promotion spots for a division, adjusted to match however many players
 * actually got relegated out of the division it promotes into. The
 * low-win-rate rule can push that division's relegation count above its
 * defined "bottom N" (e.g. 3 relegated from Master instead of 2), and the
 * same number of extra spots should open up for promotion from below.
 *
 * The live ladder (Master - Division 1 - Division 2A - Division 2B -
 * Division 3 - Division 4) is a single linear chain, so each division has
 * exactly one promotion target and vice versa. That means promotions should
 * exactly fill however many slots relegation actually vacated above, full
 * stop — not this division's own base promotion count plus the overshoot.
 * Those two base numbers aren't guaranteed to match (e.g. Division 4's base
 * promotion is 4 while Division 3's base relegation is 3), so adding the
 * overshoot on top of an already-mismatched base produced too many
 * promotions (6 instead of 5 for 5 actual relegations).
 *
 * The countFeedersInto check below is a safety net for any division outside
 * this live chain (e.g. the legacy "Division 2") that might still map onto
 * one of these targets — there, exact 1:1 matching can't be attributed to a
 * single feeder, so it falls back to topping up the base count instead.
 */
export const getEffectivePromotionSpots = (
  division: Division,
  aboveDivisionRankings: PlayerStats[] | undefined
): number => {
  const baseSpots = getPromotionSpots(division);
  const target = getPromotionTarget(division);
  if (!target || !aboveDivisionRankings || aboveDivisionRankings.length === 0) {
    return baseSpots;
  }
  const actualAboveRelegation = getActualRelegationCount(
    aboveDivisionRankings,
    target
  );

  if (countFeedersInto(target) === 1) {
    return actualAboveRelegation > 0 ? actualAboveRelegation : baseSpots;
  }

  const baseAboveRelegation = getRelegationSpots(target);
  const extra = Math.max(0, actualAboveRelegation - baseAboveRelegation);
  return baseSpots + extra;
};

/**
 * Calculate automatic promotions and relegations based on rankings
 */
export const calculatePromotionsRelegations = (
  rankings: PlayerStats[],
  division: Division
): PromotionRelegationResult[] => {
  const results: PromotionRelegationResult[] = [];

  const promotionSpots = getPromotionSpots(division);
  const relegationSpots = getRelegationSpots(division);
  const promotionTarget = getPromotionTarget(division);
  const relegationTarget = getRelegationTarget(division);

  rankings.forEach((player, index) => {
    const position = index + 1;

    // Check for promotion
    if (position <= promotionSpots && promotionTarget) {
      results.push({
        playerId: player.playerId,
        playerName: player.playerName,
        currentDivision: division,
        newDivision: promotionTarget,
        transitionType: "promoted",
        position,
        points: player.totalPoints,
      });
    }
    // Check for relegation: bottom N by position, OR below the win-rate threshold
    else if (
      (position > rankings.length - relegationSpots ||
        isLowWinRateRelegation(player)) &&
      relegationTarget
    ) {
      results.push({
        playerId: player.playerId,
        playerName: player.playerName,
        currentDivision: division,
        newDivision: relegationTarget,
        transitionType: "relegated",
        position,
        points: player.totalPoints,
      });
    }
    // Player stays in division
    else {
      results.push({
        playerId: player.playerId,
        playerName: player.playerName,
        currentDivision: division,
        newDivision: division,
        transitionType: "stays",
        position,
        points: player.totalPoints,
      });
    }
  });

  return results;
};

/**
 * Get a summary of transitions by division
 */
export const getTransitionSummary = (
  results: PromotionRelegationResult[]
): {
  promotions: number;
  relegations: number;
  stays: number;
  byDivision: Record<Division, { promotions: number; relegations: number; stays: number }>;
} => {
  const summary = {
    promotions: 0,
    relegations: 0,
    stays: 0,
    byDivision: {} as Record<Division, { promotions: number; relegations: number; stays: number }>,
  };

  results.forEach((result) => {
    if (result.transitionType === "promoted") summary.promotions++;
    if (result.transitionType === "relegated") summary.relegations++;
    if (result.transitionType === "stays") summary.stays++;

    if (!summary.byDivision[result.currentDivision]) {
      summary.byDivision[result.currentDivision] = {
        promotions: 0,
        relegations: 0,
        stays: 0,
      };
    }

    if (result.transitionType === "promoted")
      summary.byDivision[result.currentDivision].promotions++;
    if (result.transitionType === "relegated")
      summary.byDivision[result.currentDivision].relegations++;
    if (result.transitionType === "stays")
      summary.byDivision[result.currentDivision].stays++;
  });

  return summary;
};
